/*
  assets/customer.js — CUSTOMER-AND-INTERNAL-WORKSPACE-06
  Owners:
  - Safe customer-page form/table enhancement.
  - GProperty internal Lead + Buyer Recall workspace hydration for [data-agent-lead-console].

  Auth/role authority: assets/gcity-auth.js only.
  Boundaries:
  - No CSS injection, GFun, checkout, role grant, token storage or browser secret.
  - Frontend role checks are presentation guards only; internal APIs revalidate Shopify App Proxy + role server-side.
  - Buyer Recall V1 never auto-sends WhatsApp. It opens a prefilled WhatsApp URL and records agent intent/state only.
  - Never enhance Shopify password pages.
*/
(function () {
  'use strict';

  var VERSION = '2026-09-14-customer-internal-workspace-06';
  var GLOBAL_KEY = '__LITTLEG_CUSTOMER_PAGE_HELPER_VERSION__';
  var ROOT_SELECTOR = '[data-agent-lead-console]';
  var LEAD_STATUSES = Object.freeze([
    'new', 'contacted', 'viewing_scheduled', 'report_sent', 'negotiating', 'closed', 'lost'
  ]);
  var RECALL_MATCH_STATUSES = Object.freeze(['pending', 'contacted', 'ignored']);

  if (window[GLOBAL_KEY] === VERSION) return;
  window[GLOBAL_KEY] = VERSION;

  var stateByRoot = new WeakMap();

  function all(scope, selector) {
    var target = scope || document;
    return target && target.querySelectorAll
      ? Array.prototype.slice.call(target.querySelectorAll(selector))
      : [];
  }

  function clean(value) { return String(value == null ? '' : value).trim(); }
  function number(value) { var parsed = Number(value); return Number.isFinite(parsed) ? parsed : null; }
  function escapeHtml(value) {
    return clean(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function requestKey(prefix) {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return prefix + '-' + window.crypto.randomUUID();
    }
    return prefix + '-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  function money(value) {
    var parsed = number(value);
    if (parsed == null) return '-';
    try { return 'HK$' + Math.round(parsed).toLocaleString('en-HK'); }
    catch (_error) { return 'HK$' + Math.round(parsed); }
  }

  function isPasswordPage() {
    return Boolean(document.querySelector('[data-password-spatial-root], body.password, .password-page'));
  }

  function isCustomerPage() {
    if (isPasswordPage()) return false;
    return Boolean(document.querySelector('.customer, .customer-account, [data-customer-workspace], [data-glg-customer-page]'));
  }

  function enhanceForm(form) {
    if (!form || form.dataset.customerEnhanced === 'true') return;
    form.dataset.customerEnhanced = 'true';
    form.addEventListener('submit', function () {
      var submitter = form.querySelector('button[type="submit"], input[type="submit"]');
      if (!submitter) return;
      submitter.setAttribute('aria-busy', 'true');
      submitter.classList.add('is-loading');
    });
  }

  function enhanceTables(scope) {
    all(scope || document, 'table').forEach(function (table) {
      if (table.dataset.customerResponsive === 'true') return;
      table.dataset.customerResponsive = 'true';
      table.setAttribute('data-customer-table', 'true');
    });
  }

  function mountCustomerEnhancements(scope) {
    if (!isCustomerPage()) return;
    var target = scope || document;
    if (target.matches && target.matches('form')) enhanceForm(target);
    all(target, 'form').forEach(enhanceForm);
    enhanceTables(target);
  }

  function authRuntime() {
    return window.GCityAuth && typeof window.GCityAuth.requireRole === 'function'
      ? window.GCityAuth
      : null;
  }

  function authorize() {
    if (window.Shopify && window.Shopify.designMode) return Promise.resolve(true);
    var auth = authRuntime();
    if (!auth) return Promise.resolve(false);
    return Promise.resolve(auth.requireRole(['role_glg_internal'], { move: false }))
      .then(function (authState) { return Boolean(authState && authState.role === 'role_glg_internal'); })
      .catch(function () { return false; });
  }

  function normalizeLead(raw) {
    raw = raw && typeof raw === 'object' ? raw : {};
    var status = clean(raw.agent_status || raw.agentStatus || 'new').toLowerCase();
    if (LEAD_STATUSES.indexOf(status) === -1) status = 'new';
    return Object.assign({}, raw, {
      lead_id: clean(raw.lead_id || raw.leadId),
      result_id: clean(raw.result_id || raw.resultId),
      lead_grade: clean(raw.lead_grade || raw.leadGrade || 'D').toUpperCase(),
      lead_score: Number(raw.lead_score || raw.leadScore || 0) || 0,
      agent_status: status,
      estate_name: clean(raw.estate_name || raw.estateName || raw.address),
      family_fit_score: clean(raw.family_fit_score || raw.familyFitScore || raw.family_buyer_fit_score),
      created_at: clean(raw.created_at || raw.createdAt),
      updated_at: clean(raw.updated_at || raw.updatedAt)
    });
  }

  function normalizeRecallBuyer(raw) {
    raw = raw && typeof raw === 'object' ? raw : {};
    var snapshot = raw.buyer && typeof raw.buyer === 'object' ? raw.buyer : {};
    var status = clean(raw.match_status || 'pending').toLowerCase();
    if (RECALL_MATCH_STATUSES.indexOf(status) === -1) status = 'pending';
    return Object.assign({}, raw, {
      match_id: clean(raw.match_id),
      recall_id: clean(raw.recall_id),
      lead_id: clean(raw.lead_id || snapshot.lead_id),
      buyer_id: clean(raw.buyer_id || snapshot.buyer_id),
      match_status: status,
      match_reason: clean(raw.match_reason),
      match_score: Number(raw.match_score || 0) || 0,
      whatsapp_url: clean(raw.whatsapp_url),
      buyer: snapshot
    });
  }

  function normalizeRecall(raw) {
    raw = raw && typeof raw === 'object' ? raw : {};
    var property = raw.property && typeof raw.property === 'object' ? raw.property : {};
    var buyers = Array.isArray(raw.buyers) ? raw.buyers.map(normalizeRecallBuyer) : [];
    var counts = raw.counts && typeof raw.counts === 'object' ? raw.counts : {};
    return Object.assign({}, raw, {
      recall_id: clean(raw.recall_id),
      event_type: clean(raw.event_type || 'PRICE_DECREASED'),
      event_status: clean(raw.event_status || 'pending').toLowerCase(),
      property_id: clean(raw.property_id || property.property_id),
      old_price: number(raw.old_price),
      new_price: number(raw.new_price),
      matched_buyers_count: Number(raw.matched_buyers_count || buyers.length || 0),
      counts: {
        pending: Number(counts.pending || buyers.filter(function (buyer) { return buyer.match_status === 'pending'; }).length || 0),
        contacted: Number(counts.contacted || buyers.filter(function (buyer) { return buyer.match_status === 'contacted'; }).length || 0),
        ignored: Number(counts.ignored || buyers.filter(function (buyer) { return buyer.match_status === 'ignored'; }).length || 0)
      },
      property: property,
      buyers: buyers,
      created_at: clean(raw.created_at),
      updated_at: clean(raw.updated_at)
    });
  }

  function leadStatusLabel(status) {
    return ({
      new: 'New', contacted: 'Contacted', viewing_scheduled: 'Viewing Scheduled',
      report_sent: 'Report Sent', negotiating: 'Negotiating', closed: 'Closed', lost: 'Lost'
    })[status] || 'New';
  }

  function recallStatusLabel(status) {
    return ({ pending: '待聯絡', contacted: '已聯絡', ignored: '已忽略' })[status] || '待聯絡';
  }

  function emit(action, data) {
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: 'gproperty_agent_console', action: action }, data || {}));
      }
    } catch (_error) {}
  }

  function sameOriginUrl(value, fallback) {
    var raw = clean(value || fallback);
    if (!raw || raw === '#') return fallback || '#';
    try {
      var url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return fallback || '#';
      return url.pathname + url.search + url.hash;
    } catch (_error) { return fallback || '#'; }
  }

  function safeExternalUrl(value) {
    var raw = clean(value);
    if (!raw) return '#';
    try {
      var url = new URL(raw);
      return url.protocol === 'https:' ? url.toString() : '#';
    } catch (_error) { return '#'; }
  }

  function errorCode(json, fallback) {
    var error = json && json.error;
    if (error && typeof error === 'object') return clean(error.code || error.message) || fallback;
    return clean(error || (json && (json.code || json.message))) || fallback;
  }

  function readJsonResponse(response) {
    return response.json().catch(function () { return {}; }).then(function (json) {
      return { ok: response.ok, status: response.status, json: json };
    });
  }

  function addListener(target, type, listener, state) {
    if (!target) return;
    var options = state.controller ? { signal: state.controller.signal } : undefined;
    try { target.addEventListener(type, listener, options); }
    catch (_error) {
      target.addEventListener(type, listener);
      state.cleanups.push(function () { target.removeEventListener(type, listener); });
    }
  }

  function initAgentConsole(root) {
    if (!root || stateByRoot.has(root)) return;

    var state = {
      controller: typeof AbortController === 'function' ? new AbortController() : null,
      loadController: null,
      mutationControllers: new Set(),
      cleanups: [],
      active: true,
      authorized: false,
      authResolved: false,
      activeView: 'leads',
      leads: [],
      recalls: []
    };
    stateByRoot.set(root, state);
    root.dataset.agentLeadConsoleReady = VERSION;

    var cfg = {
      leadEndpoint: sameOriginUrl(root.dataset.apiEndpoint, ''),
      recallEndpoint: sameOriginUrl(root.dataset.recallEndpoint, '/apps/gcity-api/property/recalls'),
      whatsappFallback: clean(root.dataset.whatsappNumber),
      historyUrl: sameOriginUrl(root.dataset.historyUrl, '/pages/result-history'),
      contactUrl: sameOriginUrl(root.dataset.contactUrl, '/pages/contact'),
      loadingText: clean(root.dataset.loadingText) || '正在載入 GProperty 工作區…',
      loadErrorText: clean(root.dataset.loadErrorText) || '暫時未能載入 GProperty 工作區。',
      patchErrorText: clean(root.dataset.patchErrorText) || '未能更新跟進狀態。'
    };

    var workspace = root.querySelector('[data-role="workspace"]');
    var accessDenied = root.querySelector('[data-role="access-denied"]');
    var loading = root.querySelector('[data-role="loading"]');
    var error = root.querySelector('[data-role="error"]');
    var refresh = root.querySelector('[data-role="refresh"]');

    var leadView = root.querySelector('[data-role="lead-view"]');
    var recallView = root.querySelector('[data-role="recall-view"]');
    var leadList = root.querySelector('[data-role="lead-list"]');
    var recallList = root.querySelector('[data-role="recall-list"]');
    var leadSummary = root.querySelector('[data-role="lead-summary"]');
    var recallSummary = root.querySelector('[data-role="recall-summary"]');
    var gradeFilter = root.querySelector('[data-role="grade-filter"]');
    var statusFilter = root.querySelector('[data-role="status-filter"]');
    var leadSearch = root.querySelector('[data-role="lead-filter"]');
    var recallFilter = root.querySelector('[data-role="recall-filter"]');
    var viewButtons = all(root, '[data-workspace-view]');

    function headers(idempotencyKey) {
      var value = { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      if (idempotencyKey) value['X-Idempotency-Key'] = idempotencyKey;
      return value;
    }

    function requestOptions(method, body, idempotencyKey, controller, keepalive) {
      var options = {
        method: method,
        credentials: 'same-origin',
        headers: headers(idempotencyKey),
        cache: 'no-store'
      };
      if (body !== undefined) options.body = JSON.stringify(body);
      if (controller) options.signal = controller.signal;
      if (keepalive) options.keepalive = true;
      return options;
    }

    function setLoading(active) {
      if (loading) { loading.hidden = !active; loading.textContent = cfg.loadingText; }
      if (refresh) { refresh.setAttribute('aria-busy', active ? 'true' : 'false'); refresh.disabled = Boolean(active); }
    }

    function setError(message) {
      if (!error) return;
      error.textContent = clean(message);
      error.hidden = !message;
    }

    function setDenied(reason) {
      state.authorized = false;
      state.authResolved = true;
      root.dataset.accessState = 'denied';
      root.dataset.accessReason = clean(reason || 'role_not_allowed');
      if (accessDenied) accessDenied.hidden = false;
      if (workspace) workspace.hidden = true;
      setLoading(false);
      emit('access_denied', { reason: root.dataset.accessReason });
    }

    function setAllowed() {
      state.authorized = true;
      state.authResolved = true;
      root.dataset.accessState = 'allowed';
      root.removeAttribute('data-access-reason');
      if (accessDenied) accessDenied.hidden = true;
      if (workspace) workspace.hidden = false;
    }

    function setView(view) {
      state.activeView = view === 'recalls' ? 'recalls' : 'leads';
      if (leadView) leadView.hidden = state.activeView !== 'leads';
      if (recallView) recallView.hidden = state.activeView !== 'recalls';
      viewButtons.forEach(function (button) {
        var active = button.getAttribute('data-workspace-view') === state.activeView;
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.classList.toggle('agent-lead-console__action--primary', active);
      });
      root.dataset.workspaceView = state.activeView;
      emit('view_changed', { view: state.activeView });
    }

    function withIds(base, lead) {
      var route = sameOriginUrl(base, '#');
      if (!route || route === '#') return '#';
      var url = new URL(route, window.location.origin);
      if (lead.result_id) url.searchParams.set('result_id', lead.result_id);
      if (lead.lead_id) url.searchParams.set('lead_id', lead.lead_id);
      if (lead.estate_name) url.searchParams.set('estate_name', lead.estate_name);
      if (lead.family_fit_score) url.searchParams.set('family_fit_score', lead.family_fit_score);
      return url.pathname + url.search + url.hash;
    }

    function legacyWhatsappUrl(lead) {
      var phone = clean(lead.whatsapp || cfg.whatsappFallback).replace(/\D/g, '');
      var text = [
        '你好，我是 GProperty 顧問。看到你剛剛的查詢：',
        '屋苑：' + (lead.estate_name || '-'),
        '需求：' + (lead.intent || lead.owner_intent || '-'),
        '家庭適配分：' + (lead.family_fit_score || lead.family_buyer_fit_score || '-'),
        'Lead ID：' + (lead.lead_id || '-'),
        'Result ID：' + (lead.result_id || '-'),
        '我可以協助整理相近屋苑、睇樓路線或下一步資料。'
      ].join('\n');
      return phone ? 'https://wa.me/' + phone + '?text=' + encodeURIComponent(text) : withIds(cfg.contactUrl, lead);
    }

    function filteredLeads() {
      var grade = gradeFilter ? gradeFilter.value : 'all';
      var status = statusFilter ? statusFilter.value : 'all';
      var query = leadSearch ? clean(leadSearch.value).toLowerCase() : '';
      return state.leads.filter(function (lead) {
        if (grade !== 'all' && lead.lead_grade !== grade) return false;
        if (status !== 'all' && lead.agent_status !== status) return false;
        if (!query) return true;
        return [lead.lead_id, lead.result_id, lead.estate_name, lead.school_net, lead.target_school,
          lead.name, lead.whatsapp, lead.email, lead.intent, lead.owner_intent, lead.agent_status]
          .join(' ').toLowerCase().indexOf(query) !== -1;
      });
    }

    function filteredRecalls() {
      var filter = recallFilter ? recallFilter.value : 'all';
      if (filter === 'all') return state.recalls;
      return state.recalls.filter(function (recall) {
        return recall.buyers.some(function (buyer) { return buyer.match_status === filter; });
      });
    }

    function updateLeadSummary(rows) {
      if (!leadSummary) return;
      var aCount = state.leads.filter(function (lead) { return lead.lead_grade === 'A'; }).length;
      var newCount = state.leads.filter(function (lead) { return lead.agent_status === 'new'; }).length;
      var negotiating = state.leads.filter(function (lead) { return lead.agent_status === 'negotiating'; }).length;
      var closed = state.leads.filter(function (lead) { return lead.agent_status === 'closed'; }).length;
      leadSummary.innerHTML = [
        '<span>總數：' + state.leads.length + '</span>',
        '<span>目前顯示：' + rows.length + '</span>',
        '<span>A 級：' + aCount + '</span>',
        '<span>新線索：' + newCount + '</span>',
        '<span>洽談中：' + negotiating + '</span>',
        '<span>成交：' + closed + '</span>'
      ].join('');
    }

    function recallTotals() {
      var totals = { priceChanges: 0, matched: 0, pending: 0, contacted: 0, ignored: 0 };
      state.recalls.forEach(function (recall) {
        if (recall.event_type === 'PRICE_DECREASED') totals.priceChanges += 1;
        totals.matched += recall.matched_buyers_count;
        totals.pending += recall.counts.pending;
        totals.contacted += recall.counts.contacted;
        totals.ignored += recall.counts.ignored;
      });
      return totals;
    }

    function updateRecallSummary() {
      if (!recallSummary) return;
      var totals = recallTotals();
      recallSummary.innerHTML = [
        '<span>價格變動：' + totals.priceChanges + '</span>',
        '<span>符合買家數：' + totals.matched + '</span>',
        '<span>待聯絡：' + totals.pending + '</span>',
        '<span>已聯絡：' + totals.contacted + '</span>',
        '<span>已忽略：' + totals.ignored + '</span>'
      ].join('');
    }

    function briefHtml(lead) {
      var brief = lead.agent_brief && typeof lead.agent_brief === 'object' ? lead.agent_brief : {};
      var opening = clean(brief.suggested_opening) || '可先以屋苑、校網、通勤與家庭需求作切入。';
      var nextAction = clean(brief.next_action) || (lead.lead_grade === 'A'
        ? '優先完成首次聯絡，確認是否安排屋苑比較或睇樓路線。'
        : '先發送初步結果，確認是否需要顧問跟進。');
      return '<div class="agent-lead-console__brief"><strong>建議開場</strong><br>' + escapeHtml(opening)
        + '<br><br><strong>下一步</strong><br>' + escapeHtml(nextAction) + '</div>';
    }

    function leadHtml(lead) {
      var title = lead.estate_name || lead.school_net || lead.target_school || '未指定屋苑';
      var score = lead.family_fit_score || lead.family_buyer_fit_score || '-';
      var status = LEAD_STATUSES.indexOf(lead.agent_status) !== -1 ? lead.agent_status : 'new';
      var historyUrl = withIds(cfg.historyUrl, lead);
      var contactUrl = withIds(cfg.contactUrl, lead);
      return '<article class="agent-lead-console__lead" data-lead-id="' + escapeHtml(lead.lead_id)
        + '" data-result-id="' + escapeHtml(lead.result_id) + '">'
        + '<div class="agent-lead-console__lead-top"><div><h2>' + escapeHtml(title) + '</h2><div class="agent-lead-console__meta">'
        + '<span class="agent-lead-console__pill agent-lead-console__grade">Grade ' + escapeHtml(lead.lead_grade) + '</span>'
        + '<span class="agent-lead-console__pill">Lead ' + escapeHtml(lead.lead_score || '-') + '</span>'
        + '<span class="agent-lead-console__pill">Fit ' + escapeHtml(score) + '</span>'
        + '<span class="agent-lead-console__pill agent-lead-console__status" data-status="' + escapeHtml(status) + '">' + escapeHtml(leadStatusLabel(status)) + '</span>'
        + '</div></div><div class="agent-lead-console__meta">'
        + '<span class="agent-lead-console__pill">' + escapeHtml(lead.intent || lead.owner_intent || '-') + '</span>'
        + '<span class="agent-lead-console__pill">' + escapeHtml(lead.budget || lead.expected_price || '-') + '</span>'
        + '<span class="agent-lead-console__pill">' + escapeHtml(lead.move_timeline || lead.created_at || '-') + '</span>'
        + '</div></div><div class="agent-lead-console__meta">'
        + (lead.lead_id ? '<span class="agent-lead-console__pill">Lead ID: ' + escapeHtml(lead.lead_id) + '</span>' : '')
        + (lead.result_id ? '<span class="agent-lead-console__pill">Result ID: ' + escapeHtml(lead.result_id) + '</span>' : '')
        + (lead.whatsapp ? '<span class="agent-lead-console__pill">WhatsApp: ' + escapeHtml(lead.whatsapp) + '</span>' : '')
        + (lead.email ? '<span class="agent-lead-console__pill">Email: ' + escapeHtml(lead.email) + '</span>' : '')
        + '</div>' + briefHtml(lead)
        + '<div class="agent-lead-console__actions">'
        + '<a class="agent-lead-console__action agent-lead-console__action--primary" href="' + escapeHtml(legacyWhatsappUrl(lead)) + '" target="_blank" rel="noopener noreferrer" data-agent-action="whatsapp">WhatsApp 跟進</a>'
        + '<a class="agent-lead-console__action" href="' + escapeHtml(historyUrl) + '" data-agent-action="history">結果記錄</a>'
        + '<a class="agent-lead-console__action" href="' + escapeHtml(contactUrl) + '" data-agent-action="contact">聯絡頁</a>'
        + '<div class="agent-lead-console__status-actions">'
        + '<button class="agent-lead-console__action" type="button" data-action="lead-status" data-status="contacted">已聯絡</button>'
        + '<button class="agent-lead-console__action" type="button" data-action="lead-status" data-status="viewing_scheduled">已約睇樓</button>'
        + '<button class="agent-lead-console__action" type="button" data-action="lead-status" data-status="report_sent">已發報告</button>'
        + '<button class="agent-lead-console__action" type="button" data-action="lead-status" data-status="negotiating">洽談中</button>'
        + '<button class="agent-lead-console__action" type="button" data-action="lead-status" data-status="closed">成交</button>'
        + '<button class="agent-lead-console__action" type="button" data-action="lead-status" data-status="lost">Lost</button>'
        + '</div></div></article>';
    }

    function recallBuyerHtml(recall, buyer) {
      var snapshot = buyer.buyer || {};
      var label = clean(snapshot.name || snapshot.whatsapp || snapshot.email || buyer.lead_id) || 'Buyer';
      var whatsapp = safeExternalUrl(buyer.whatsapp_url);
      var canContact = buyer.match_status !== 'contacted' && whatsapp !== '#';
      var canIgnore = buyer.match_status !== 'ignored';
      return '<div class="agent-lead-console__brief" data-recall-buyer data-recall-id="' + escapeHtml(recall.recall_id)
        + '" data-lead-id="' + escapeHtml(buyer.lead_id) + '">'
        + '<div class="agent-lead-console__lead-top"><div><strong>' + escapeHtml(label) + '</strong><div class="agent-lead-console__meta">'
        + '<span class="agent-lead-console__pill">' + escapeHtml(buyer.match_reason || 'matched') + '</span>'
        + '<span class="agent-lead-console__pill">Match ' + escapeHtml(buyer.match_score) + '</span>'
        + '<span class="agent-lead-console__pill agent-lead-console__status" data-recall-status="' + escapeHtml(buyer.match_status) + '">' + escapeHtml(recallStatusLabel(buyer.match_status)) + '</span>'
        + '</div></div></div>'
        + '<div class="agent-lead-console__actions">'
        + (canContact
          ? '<a class="agent-lead-console__action agent-lead-console__action--primary" href="' + escapeHtml(whatsapp) + '" target="_blank" rel="noopener noreferrer" data-recall-action="contacted">WhatsApp 聯絡</a>'
          : '<span class="agent-lead-console__pill">' + (buyer.match_status === 'contacted' ? '已聯絡' : '沒有 WhatsApp') + '</span>')
        + (canIgnore ? '<button type="button" class="agent-lead-console__action" data-recall-action="ignored">忽略</button>' : '')
        + '</div></div>';
    }

    function recallHtml(recall) {
      var property = recall.property || {};
      var title = clean(property.title || property.estate_name || property.address || recall.property_id) || '未命名物業';
      var buyers = recall.buyers || [];
      return '<article class="agent-lead-console__lead" data-recall-card data-recall-id="' + escapeHtml(recall.recall_id) + '">'
        + '<div class="agent-lead-console__lead-top"><div><h2>' + escapeHtml(title) + '</h2><div class="agent-lead-console__meta">'
        + '<span class="agent-lead-console__pill agent-lead-console__grade">價格變動</span>'
        + '<span class="agent-lead-console__pill">' + escapeHtml(money(recall.old_price)) + ' → ' + escapeHtml(money(recall.new_price)) + '</span>'
        + '<span class="agent-lead-console__pill">符合買家數 ' + escapeHtml(recall.matched_buyers_count) + '</span>'
        + '</div></div><div class="agent-lead-console__meta">'
        + '<span class="agent-lead-console__pill">待聯絡 ' + escapeHtml(recall.counts.pending) + '</span>'
        + '<span class="agent-lead-console__pill">已聯絡 ' + escapeHtml(recall.counts.contacted) + '</span>'
        + '<span class="agent-lead-console__pill">已忽略 ' + escapeHtml(recall.counts.ignored) + '</span>'
        + '</div></div>'
        + (buyers.length ? buyers.map(function (buyer) { return recallBuyerHtml(recall, buyer); }).join('')
          : '<div class="agent-lead-console__state">目前沒有符合的過往買家。</div>')
        + '</article>';
    }

    function renderLeads() {
      var rows = filteredLeads();
      updateLeadSummary(rows);
      if (!leadList) return;
      leadList.innerHTML = rows.length
        ? rows.map(leadHtml).join('')
        : '<div class="agent-lead-console__state">暫時沒有符合條件的線索。</div>';
    }

    function renderRecalls() {
      var rows = filteredRecalls();
      updateRecallSummary();
      if (!recallList) return;
      recallList.innerHTML = rows.length
        ? rows.map(recallHtml).join('')
        : '<div class="agent-lead-console__state">暫時沒有 Buyer Recall 事件。</div>';
    }

    function render() {
      if (!state.active) return;
      renderLeads();
      renderRecalls();
      setView(state.activeView);
    }

    function loadWorkspace() {
      if (!state.authorized || !cfg.leadEndpoint || !cfg.recallEndpoint || !state.active) {
        setError(cfg.loadErrorText);
        return;
      }
      if (state.loadController) state.loadController.abort();
      state.loadController = typeof AbortController === 'function' ? new AbortController() : null;
      var controller = state.loadController;
      setLoading(true);
      setError('');

      Promise.all([
        fetch(cfg.leadEndpoint, requestOptions('GET', undefined, '', controller)).then(readJsonResponse),
        fetch(cfg.recallEndpoint, requestOptions('GET', undefined, '', controller)).then(readJsonResponse)
      ]).then(function (responses) {
        if (!state.active) return;
        var leadsResponse = responses[0];
        var recallsResponse = responses[1];
        if (!leadsResponse.ok || leadsResponse.json.success === false) throw new Error(errorCode(leadsResponse.json, 'lead_load_failed'));
        if (!recallsResponse.ok || recallsResponse.json.success === false) throw new Error(errorCode(recallsResponse.json, 'recall_load_failed'));
        state.leads = Array.isArray(leadsResponse.json.leads) ? leadsResponse.json.leads.map(normalizeLead) : [];
        state.recalls = Array.isArray(recallsResponse.json.recalls) ? recallsResponse.json.recalls.map(normalizeRecall) : [];
        render();
        emit('workspace_loaded', { leads: state.leads.length, recalls: state.recalls.length });
      }).catch(function (requestError) {
        if (!requestError || requestError.name !== 'AbortError') {
          setError(cfg.loadErrorText);
          emit('workspace_load_failed', { error: requestError && requestError.message ? requestError.message : 'unknown' });
        }
      }).finally(function () {
        if (state.loadController === controller) state.loadController = null;
        if (state.active) setLoading(false);
      });
    }

    function patchLeadStatus(leadId, status, trigger) {
      if (!state.authorized || !leadId || LEAD_STATUSES.indexOf(status) === -1 || !cfg.leadEndpoint || !state.active) return;
      setError('');
      if (trigger) { trigger.disabled = true; trigger.setAttribute('aria-busy', 'true'); }
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      if (controller) state.mutationControllers.add(controller);
      var idempotencyKey = requestKey('gproperty-status-' + leadId + '-' + status);
      var endpoint = cfg.leadEndpoint.replace(/\/$/, '') + '/' + encodeURIComponent(leadId);
      fetch(endpoint, requestOptions('PATCH', { agent_status: status }, idempotencyKey, controller))
        .then(readJsonResponse)
        .then(function (response) {
          if (!state.active) return;
          if (!response.ok || response.json.success === false) throw new Error(errorCode(response.json, 'patch_failed'));
          var updated = normalizeLead(response.json.lead || { lead_id: leadId, agent_status: status });
          state.leads = state.leads.map(function (lead) { return lead.lead_id === leadId ? Object.assign({}, lead, updated) : lead; });
          renderLeads();
          emit('lead_status_changed', { lead_id: leadId, agent_status: status, result_id: updated.result_id || '' });
        }).catch(function (requestError) {
          if (!requestError || requestError.name !== 'AbortError') {
            setError(cfg.patchErrorText);
            emit('lead_status_change_failed', { lead_id: leadId, agent_status: status, error: requestError && requestError.message ? requestError.message : 'unknown' });
          }
        }).finally(function () {
          if (controller) state.mutationControllers.delete(controller);
          if (trigger && document.contains(trigger)) { trigger.disabled = false; trigger.removeAttribute('aria-busy'); }
        });
    }

    function replaceRecall(updatedRecall) {
      var normalized = normalizeRecall(updatedRecall);
      state.recalls = state.recalls.map(function (recall) {
        return recall.recall_id === normalized.recall_id ? normalized : recall;
      });
      renderRecalls();
    }

    function recallAction(recallId, leadId, action, trigger, background) {
      if (!state.authorized || !recallId || !leadId || RECALL_MATCH_STATUSES.indexOf(action) === -1 || action === 'pending' || !state.active) return;
      var idempotencyKey = requestKey('gproperty-recall-' + recallId + '-' + leadId + '-' + action);
      if (trigger && !background) { trigger.disabled = true; trigger.setAttribute('aria-busy', 'true'); }
      var controller = background ? null : (typeof AbortController === 'function' ? new AbortController() : null);
      if (controller) state.mutationControllers.add(controller);
      fetch(cfg.recallEndpoint, requestOptions('POST', {
        action: action,
        recall_id: recallId,
        lead_id: leadId
      }, idempotencyKey, controller, Boolean(background)))
        .then(readJsonResponse)
        .then(function (response) {
          if (!state.active) return;
          if (!response.ok || response.json.success === false) throw new Error(errorCode(response.json, 'recall_action_failed'));
          if (response.json.recall) replaceRecall(response.json.recall);
          emit('recall_' + action, { recall_id: recallId, lead_id: leadId, auto_send: false });
        }).catch(function (requestError) {
          if (!requestError || requestError.name !== 'AbortError') {
            setError(cfg.patchErrorText);
            emit('recall_action_failed', { recall_id: recallId, lead_id: leadId, action: action, error: requestError && requestError.message ? requestError.message : 'unknown' });
          }
        }).finally(function () {
          if (controller) state.mutationControllers.delete(controller);
          if (trigger && !background && document.contains(trigger)) { trigger.disabled = false; trigger.removeAttribute('aria-busy'); }
        });
    }

    function resolveAuthorization() {
      authorize().then(function (allowed) {
        if (!state.active) return;
        if (!allowed) { setDenied('role_not_allowed'); return; }
        setAllowed();
        if (window.Shopify && window.Shopify.designMode) {
          root.dataset.previewState = 'design-mode-no-live-fetch';
          setLoading(false);
          render();
          return;
        }
        loadWorkspace();
      });
    }

    addListener(refresh, 'click', loadWorkspace, state);
    addListener(gradeFilter, 'change', renderLeads, state);
    addListener(statusFilter, 'change', renderLeads, state);
    addListener(leadSearch, 'input', renderLeads, state);
    addListener(recallFilter, 'change', renderRecalls, state);
    viewButtons.forEach(function (button) {
      addListener(button, 'click', function () { setView(button.getAttribute('data-workspace-view')); }, state);
    });
    addListener(document, 'gcity:auth:ready', function () {
      if (!state.authResolved || !state.authorized) resolveAuthorization();
    }, state);

    addListener(root, 'click', function (event) {
      var target = event.target && event.target.closest ? event.target : null;
      if (!target) return;

      var leadStatusButton = target.closest('[data-action="lead-status"]');
      if (leadStatusButton && root.contains(leadStatusButton)) {
        var leadCard = leadStatusButton.closest('[data-lead-id]');
        if (leadCard) patchLeadStatus(leadCard.getAttribute('data-lead-id'), leadStatusButton.getAttribute('data-status'), leadStatusButton);
        return;
      }

      var recallActionTarget = target.closest('[data-recall-action]');
      if (recallActionTarget && root.contains(recallActionTarget)) {
        var buyerRow = recallActionTarget.closest('[data-recall-buyer]');
        if (!buyerRow) return;
        var recallId = buyerRow.getAttribute('data-recall-id');
        var leadId = buyerRow.getAttribute('data-lead-id');
        var action = recallActionTarget.getAttribute('data-recall-action');
        if (recallActionTarget.tagName === 'A' && action === 'contacted') {
          recallAction(recallId, leadId, action, recallActionTarget, true);
          return;
        }
        event.preventDefault();
        recallAction(recallId, leadId, action, recallActionTarget, false);
        return;
      }

      var actionLink = target.closest('[data-agent-action]');
      if (actionLink && root.contains(actionLink)) {
        var card = actionLink.closest('[data-lead-id]');
        emit(actionLink.getAttribute('data-agent-action'), {
          lead_id: card ? card.getAttribute('data-lead-id') : '',
          result_id: card ? card.getAttribute('data-result-id') : '',
          href: actionLink.getAttribute('href') || ''
        });
      }
    }, state);

    setView('leads');
    resolveAuthorization();
  }

  function destroyAgentConsole(root) {
    var state = root ? stateByRoot.get(root) : null;
    if (!state) return;
    state.active = false;
    if (state.controller) state.controller.abort();
    if (state.loadController) state.loadController.abort();
    state.mutationControllers.forEach(function (controller) { controller.abort(); });
    state.mutationControllers.clear();
    state.cleanups.forEach(function (cleanup) { cleanup(); });
    stateByRoot.delete(root);
    root.removeAttribute('data-agent-lead-console-ready');
    root.removeAttribute('data-access-state');
    root.removeAttribute('data-access-reason');
    root.removeAttribute('data-workspace-view');
  }

  function mount(scope) {
    var target = scope || document;
    mountCustomerEnhancements(target);
    if (target.matches && target.matches(ROOT_SELECTOR)) initAgentConsole(target);
    all(target, ROOT_SELECTOR).forEach(initAgentConsole);
  }

  function destroy(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) destroyAgentConsole(target);
    all(target, ROOT_SELECTOR).forEach(destroyAgentConsole);
  }

  document.addEventListener('shopify:section:load', function (event) { mount(event.target || document); });
  document.addEventListener('shopify:section:unload', function (event) { destroy(event.target || document); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(document); }, { once: true });
  } else {
    mount(document);
  }
})();
